/**
 * NREL Solar Resource API — solar irradiance data.
 * Free API key required (register at developer.nrel.gov).
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout } from './types';

export interface SolarData {
  ghi: number;    // kWh/m²/day Global Horizontal Irradiance (annual avg)
  dni: number;    // kWh/m²/day Direct Normal Irradiance (annual avg)
}

/**
 * Bucketed outcome of an NREL Solar lookup — drives the
 * `[provider:nrel:solar]` telemetry breadcrumb that Cloud Run logs grep.
 *
 * Mirrors the EL pack's `[provider:elevation]` taxonomy so PU1
 * diagnostics line up across providers.
 *
 *   - 'ok'            — HTTP 200 with finite GHI/DNI values (NREL primary)
 *   - 'ok-openmeteo'  — NREL failed but the Open-Meteo fallback recovered GHI
 *   - 'http-error'    — non-200 from the upstream
 *   - 'empty-outputs' — HTTP 200 but `json.outputs` missing / not an object
 *   - 'parse-error'   — JSON parse failed
 *   - 'network-error' — fetch threw / aborted
 *   - 'no-value'      — `outputs.avg_ghi.annual` not numeric
 *   - 'all-failed'    — both NREL and Open-Meteo exhausted (cell stays empty)
 */
type NrelSolarOutcomeBucket =
  | 'ok'
  | 'ok-openmeteo'
  | 'http-error'
  | 'empty-outputs'
  | 'parse-error'
  | 'network-error'
  | 'no-value'
  | 'all-failed';

function logSolarTelemetry(
  phase: 'request' | 'response',
  payload: Record<string, unknown>,
): void {
  // eslint-disable-next-line no-console
  console.info(`[provider:nrel:solar] ${phase}`, payload);
}

export const nrelSolarProvider: DataProvider<SolarData> = {
  id: 'nrel-solar',
  name: 'NREL Solar Resource Data',
  cacheTtlMs: CACHE_TTL.CLIMATE_NORMALS,
  requiresKey: false, // Key injected server-side by proxy

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<SolarData>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`nrel-solar` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. Solar irradiance varies slowly — the centroid generally lands
    // in the same NSRDB grid cell as the address — so this is a
    // coordinate-consistency change; the parcel centre is the right point.
    let ghi: number | null = null;
    let dni = 0;
    let source = '';
    // PRR-4 — preserve a key/auth failure so that, IF the Open-Meteo fallback
    // ALSO fails, the gap recommendation can still name `NREL_API_KEY` (the
    // auth-missing / auth-invalid tier) rather than the generic transient tier.
    let nrelAuthError: Error | null = null;

    // Attempt 1: NREL Solar Resource (NSRDB — gold standard for US sites).
    // api_key injected by server proxy (SERVER_API_KEYS['developer.nrel.gov']).
    const url = `https://developer.nrel.gov/api/solar/solar_resource/v1.json?lat=${lat}&lon=${lng}`;
    logSolarTelemetry('request', {
      caller: 'nrelSolarProvider.fetch.attempt-nrel',
      lat,
      lng,
      upstream: 'developer.nrel.gov/api/solar/solar_resource/v1.json',
    });
    try {
      const res = await fetchWithTimeout(url, 15_000, geo?.signal);
      if (res.ok) {
        let json: { outputs?: { avg_ghi?: { annual?: number }; avg_dni?: { annual?: number } } } = {};
        try {
          json = await res.json();
        } catch (e) {
          logSolarTelemetry('response', {
            caller: 'nrelSolarProvider.fetch.attempt-nrel',
            bucket: 'parse-error' as NrelSolarOutcomeBucket,
            error: (e as Error)?.message ?? 'unknown',
          });
          json = {};
        }
        const outputs = json.outputs;
        const g = outputs?.avg_ghi?.annual;
        const d = outputs?.avg_dni?.annual;
        if (typeof g === 'number' && Number.isFinite(g)) {
          ghi = parseFloat(g.toFixed(2));
          dni = parseFloat((typeof d === 'number' && Number.isFinite(d) ? d : 0).toFixed(2));
          source = 'NREL Solar Resource API';
          logSolarTelemetry('response', {
            caller: 'nrelSolarProvider.fetch.attempt-nrel',
            bucket: 'ok' as NrelSolarOutcomeBucket,
            ghi,
            dni,
          });
        } else {
          logSolarTelemetry('response', {
            caller: 'nrelSolarProvider.fetch.attempt-nrel',
            bucket: (outputs ? 'no-value' : 'empty-outputs') as NrelSolarOutcomeBucket,
            rawGhi: g ?? null,
            rawShape: Object.keys(json ?? {}),
          });
        }
      } else {
        // PRR-4 — distinguish "API key not configured in Cloud Run" from
        // "API key was sent and upstream rejected it". The proxy stamps
        // `X-DA-Key-Source: none` when neither a personal header nor the org
        // env var is set. We RECORD (not throw) so the fallback still runs.
        const keySource = res.headers.get('X-DA-Key-Source') ?? '';
        const isAuth = res.status === 401 || res.status === 403;
        logSolarTelemetry('response', {
          caller: 'nrelSolarProvider.fetch.attempt-nrel',
          bucket: 'http-error' as NrelSolarOutcomeBucket,
          httpStatus: res.status,
          keySource: keySource || null,
        });
        if (isAuth && keySource === 'none') {
          nrelAuthError = new Error('NREL Solar: API key not configured (NREL_API_KEY)');
        } else if (isAuth) {
          nrelAuthError = new Error(`NREL Solar: API key rejected (${res.status})`);
        }
      }
    } catch (e) {
      logSolarTelemetry('response', {
        caller: 'nrelSolarProvider.fetch.attempt-nrel',
        bucket: 'network-error' as NrelSolarOutcomeBucket,
        error: (e as Error)?.message ?? 'unknown',
      });
      /* try fallback */
    }

    // Attempt 2: Open-Meteo historical solar radiation (ERA5 — free, no key,
    // globally available). Mirrors the nrel-wind → Open-Meteo fallback so the
    // solar cell fills even when the NREL endpoint is unreachable. Annual GHI
    // from the hourly shortwave-radiation mean: a W/m² value held over one hour
    // is W·h/m²; the annual daily-average reduces to mean(W/m²) × 24 h ÷ 1000
    // = kWh/m²/day (since Σhourly / N × 8760h / 365d ≡ mean × 24h). Same for
    // DNI from direct_normal_irradiance.
    const WM2_TO_KWH_M2_DAY = 24 / 1000;
    if (ghi == null) {
      const omUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&start_date=2023-01-01&end_date=2023-12-31&hourly=shortwave_radiation,direct_normal_irradiance&timezone=auto`;
      logSolarTelemetry('request', {
        caller: 'nrelSolarProvider.fetch.attempt-openmeteo',
        lat,
        lng,
        upstream: 'archive-api.open-meteo.com',
      });
      try {
        const res = await fetchWithTimeout(omUrl, 20_000, geo?.signal);
        if (res.ok) {
          let json: {
            hourly?: {
              shortwave_radiation?: Array<number | null>;
              direct_normal_irradiance?: Array<number | null>;
            };
          } = {};
          try {
            json = await res.json();
          } catch (e) {
            logSolarTelemetry('response', {
              caller: 'nrelSolarProvider.fetch.attempt-openmeteo',
              bucket: 'parse-error' as NrelSolarOutcomeBucket,
              error: (e as Error)?.message ?? 'unknown',
            });
            json = {};
          }
          const swr = (json.hourly?.shortwave_radiation ?? []).filter(
            (v): v is number => v != null && Number.isFinite(v) && v >= 0,
          );
          if (swr.length > 0) {
            const meanGhiWm2 = swr.reduce((s, v) => s + v, 0) / swr.length;
            ghi = parseFloat((meanGhiWm2 * WM2_TO_KWH_M2_DAY).toFixed(2));
            const dniArr = (json.hourly?.direct_normal_irradiance ?? []).filter(
              (v): v is number => v != null && Number.isFinite(v) && v >= 0,
            );
            if (dniArr.length > 0) {
              const meanDniWm2 = dniArr.reduce((s, v) => s + v, 0) / dniArr.length;
              dni = parseFloat((meanDniWm2 * WM2_TO_KWH_M2_DAY).toFixed(2));
            }
            source = 'Open-Meteo Historical Solar (ERA5 shortwave radiation, hourly mean)';
            logSolarTelemetry('response', {
              caller: 'nrelSolarProvider.fetch.attempt-openmeteo',
              bucket: 'ok-openmeteo' as NrelSolarOutcomeBucket,
              ghi,
              dni,
              samples: swr.length,
            });
          } else {
            logSolarTelemetry('response', {
              caller: 'nrelSolarProvider.fetch.attempt-openmeteo',
              bucket: 'empty-outputs' as NrelSolarOutcomeBucket,
              reason: 'hourly-shortwave-empty',
            });
          }
        } else {
          logSolarTelemetry('response', {
            caller: 'nrelSolarProvider.fetch.attempt-openmeteo',
            bucket: 'http-error' as NrelSolarOutcomeBucket,
            httpStatus: res.status,
          });
        }
      } catch (e) {
        logSolarTelemetry('response', {
          caller: 'nrelSolarProvider.fetch.attempt-openmeteo',
          bucket: 'network-error' as NrelSolarOutcomeBucket,
          error: (e as Error)?.message ?? 'unknown',
        });
        /* exhausted */
      }
    }

    if (ghi == null || !Number.isFinite(ghi)) {
      logSolarTelemetry('response', {
        caller: 'nrelSolarProvider.fetch',
        bucket: 'all-failed' as NrelSolarOutcomeBucket,
        nrelAuth: nrelAuthError?.message ?? null,
      });
      // PRR-4 — propagate the specific NREL auth signal when Open-Meteo also
      // failed, so the gap recommendation names `NREL_API_KEY` directly.
      if (nrelAuthError) throw nrelAuthError;
      throw new Error('Solar data: all sources failed');
    }

    return {
      data: {
        ghi: parseFloat(ghi.toFixed(2)),
        dni: parseFloat(dni.toFixed(2)),
      },
      sources: [{
        field: 'solar',
        source: `${source} — GHI: ${ghi.toFixed(2)} kWh/m²/day, DNI: ${dni.toFixed(2)} kWh/m²/day`,
        url: source.includes('Open-Meteo')
          ? 'https://open-meteo.com/'
          : 'https://developer.nrel.gov/docs/solar/solar-resource-v1/',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
